import { toast } from '@approvalmax/ui/src/components';
import { errorHelpers, hooks } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { domain, stateTree } from 'modules/data';
import { integrationActions } from 'modules/integration';
import { useDispatch, useSelector, useShallowEqualSelector } from 'modules/react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import {
    cancelActivePopup,
    createNewReport,
    updateDynamicReportColumns,
    updateFiltersPopupReportConfig,
} from '../../actions';
import ColumnKind from '../../config/standard/columnKind';
import { ReferenceListFilter } from '../../data/filters/referenceListFilter';
import { newReportPopupId } from '../../reducers/page/newReportPopupReducer';
import { ReportFiltersPopupData } from '../../reducers/page/reportFiltersPopupReducer';
import { getActivePopup, getReportType } from '../../selectors/pageSelectors';
import { ReportConfig } from '../../types/ReportConfig';
import { messages } from './ReportCreatePopup.messages';
import { ActiveStep } from './ReportCreatePopup.types';

export const useCreateReportPopup = () => {
    const dispatch = useDispatch();
    const popup = useSelector(getActivePopup);

    const handleClose = useCallback(() => {
        dispatch(cancelActivePopup());
    }, [dispatch]);

    return {
        isOpen: popup?.id === newReportPopupId,
        handleClose,
    };
};

export const useReportCreatePopupConfig = () => {
    const dispatch = useDispatch();

    const popup = useSelector<ReportFiltersPopupData | undefined>(getActivePopup);

    const handleChangeReportConfig = useCallback(
        (reportConfig: ReportConfig) => {
            dispatch(updateFiltersPopupReportConfig(reportConfig));
        },
        [dispatch]
    );

    return {
        reportConfig: popup?.reportConfig || null,
        handleChangeReportConfig,
    };
};

export const useReportCreatePopup = (reportConfig: ReportConfig | null) => {
    const dispatch = useDispatch();

    const [isCreating, setIsCreating] = useState(false);

    const company = useCompanyBasedOnUrl();

    const reportType = useSelector(getReportType);
    const integration = useSelector((state) => selectors.integration.getIntegrationByCompanyId(state, company.id));

    const isLoading = useShallowEqualSelector((state) => {
        const hasActiveIntegration = selectors.integration.hasActiveIntegration(state, company.id);

        const isLoadingIntegrationFields =
            hasActiveIntegration &&
            selectors.field.fieldSetIsLoading(state, {
                type: stateTree.FieldSetType.AllIntegrationFields,
                companyId: company.id,
            });

        const isLoadingTemplateFields = selectors.field.fieldSetIsLoading(state, {
            type: stateTree.FieldSetType.TemplateFields,
            companyId: company.id,
            templateIntegrationCode: null,
        });

        return isLoadingIntegrationFields || isLoadingTemplateFields;
    });

    const unmountedRef = hooks.useUnmountedRef();
    const prevLoadingData = hooks.usePrevious(isLoading);

    const templateColumn = useMemo(
        () => reportConfig?.columns.find((column) => column.kind === ColumnKind.TemplateId),
        [reportConfig?.columns]
    );

    const handleCreateReport = useCallback(async () => {
        if (!reportConfig) {
            return;
        }

        setIsCreating(true);

        try {
            await dispatch(createNewReport(reportConfig));
        } finally {
            if (!unmountedRef.current) {
                setIsCreating(false);
            }
        }
    }, [dispatch, reportConfig, unmountedRef]);

    useEffect(() => {
        if (isLoading) {
            dispatch(integrationActions.loadAllCompanyFields({ companyId: company.id }));
        }
    }, [company.id, dispatch, isLoading]);

    useEffect(() => {
        if (!reportConfig) {
            return;
        }

        const isQboLineItem =
            reportType === domain.ReportType.LineItem && integration?.integrationType === domain.IntegrationType.QBooks;

        // Load the data. Each time the data has loaded - adjust the report config
        if (!isQboLineItem && !isLoading && (prevLoadingData || prevLoadingData == null)) {
            dispatch(updateDynamicReportColumns({ reportConfig }));
        }
    }, [company.id, dispatch, integration?.integrationType, isLoading, prevLoadingData, reportConfig, reportType]);

    const isValueSelected = !!(
        (reportType === domain.ReportType.Request && (templateColumn?.filter as ReferenceListFilter)?.values.length) ||
        (reportType === domain.ReportType.LineItem && reportConfig?.reportCode)
    );

    return {
        isCreating,
        isLoading,
        isValueSelected,
        handleCreateReport,
    };
};

export const useReportCreatePopupSteps = (reportConfig: ReportConfig | null, createReport: () => void) => {
    const [activeStep, setActiveStep] = useState(ActiveStep.ReportType);
    const [isErrorsDisplayed, setIsErrorsDisplayed] = useState(false);

    const handleGoToPrevStep = useCallback(() => {
        switch (activeStep) {
            case ActiveStep.ReportType:
                break;

            case ActiveStep.Filters:
                setActiveStep(ActiveStep.ReportType);
                break;

            case ActiveStep.VisibleColumns:
                setActiveStep(ActiveStep.Filters);
                break;

            case ActiveStep.ReportName:
                setActiveStep(ActiveStep.VisibleColumns);
                break;

            default:
                throw errorHelpers.assertNever(activeStep);
        }
    }, [activeStep]);

    const handleGoToNextStep = useCallback(() => {
        switch (activeStep) {
            case ActiveStep.ReportType:
                setActiveStep(ActiveStep.Filters);
                break;

            case ActiveStep.Filters:
                setActiveStep(ActiveStep.VisibleColumns);
                break;

            case ActiveStep.VisibleColumns:
                setActiveStep(ActiveStep.ReportName);
                break;

            case ActiveStep.ReportName: {
                if (!reportConfig?.name.trim()) {
                    toast.error(messages.errorReportNameRequired);
                    setIsErrorsDisplayed(true);

                    return;
                }

                setIsErrorsDisplayed(false);

                createReport();

                break;
            }

            default:
                throw errorHelpers.assertNever(activeStep);
        }
    }, [activeStep, createReport, reportConfig?.name]);

    return {
        activeStep,
        handleGoToPrevStep,
        handleGoToNextStep,
        isErrorsDisplayed,
        isFirstStep: activeStep === ActiveStep.ReportType,
        isLastStep: activeStep === ActiveStep.ReportName,
    };
};
